import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import type {
  ApplicationStatusMock,
  LenderRowStatusMock,
  OfferDecisionMock,
} from '../mocks';

/**
 * Status pill used across the carrier applications table, the offer
 * cards in row expansion, and the lender inbox. Three discriminated
 * `kind`s so each surface uses its own status enum and translation
 * subtree.
 */
export type StatusBadgeProps =
  | { kind: 'application'; value: ApplicationStatusMock; className?: string }
  | { kind: 'offer'; value: OfferDecisionMock; className?: string }
  | { kind: 'lender_row'; value: LenderRowStatusMock; className?: string };

const APPLICATION_TONE: Record<ApplicationStatusMock, string> = {
  pending_review: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200',
  submitted: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',
  declined: 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-200',
  accepted: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200',
  funded: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200',
  awaiting_repayment_approval:
    'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',
  repaid: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200',
  defaulted: 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-200',
  cancelled: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200',
};

const OFFER_TONE: Record<OfferDecisionMock, string> = {
  pending: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',
  interested: 'bg-blue-50 text-blue-800 ring-1 ring-inset ring-blue-200',
  offered: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200',
  declined: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200',
  accepted: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200',
};

const LENDER_ROW_TONE: Record<LenderRowStatusMock, string> = {
  pending: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',
  you_approved: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200',
  you_declined: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200',
  won: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200',
  lost: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200',
  funded: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200',
  repaid: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200',
  defaulted: 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-200',
};

export function StatusBadge(props: StatusBadgeProps) {
  const { t } = useTranslation('m-financing');
  const { className } = props;

  const tone =
    props.kind === 'application'
      ? APPLICATION_TONE[props.value]
      : props.kind === 'offer'
        ? OFFER_TONE[props.value]
        : LENDER_ROW_TONE[props.value];

  const label = t(`status.${props.kind}.${props.value}`);

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        tone,
        className,
      )}>
      {label}
    </span>
  );
}
